import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X, Sun, Moon, Heart, Shield, Settings, LayoutDashboard, LogOut } from 'lucide-react';
import { authAPI, cartAPI, wishlistAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/contexts/ThemeContext';

const Header = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    try {
      const userData = await authAPI.getMe();
      setUser(userData);
      setIsAuthenticated(true);
      loadCart();
      loadWishlist();
      checkAdminStatus();
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const loadCart = async () => {
    try {
      const cart = await cartAPI.getCart();
      setCartCount(cart.length);
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  };

  const loadWishlist = async () => {
    try {
      const wishlist = await wishlistAPI.getWishlist();
      setWishlistCount(wishlist.length);
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/check`, {
        credentials: 'include'
      });
      const data = await response.json();
      setIsAdmin(data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      setIsAuthenticated(false);
      setUser(null);
      setIsAdmin(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="glassmorphism-header sticky top-0 z-50" data-testid="main-header" style={{ background: theme === 'light' ? 'var(--color-surface)' : 'var(--color-primary)', borderBottom: '1px solid var(--color-divider)' }}>
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <div className="text-2xl font-bold tracking-tight" style={{ color: 'var(--color-accent)' }} title="Bamo Tech Sphere">
              BTS<span style={{ color: 'var(--color-text-primary)' }} className="hidden sm:inline"> | Bamo Tech Sphere</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/products" className="text-sm font-medium text-foreground hover:text-accent transition-colors" data-testid="nav-products">
              Products
            </Link>
            <Link to="/categories" className="text-sm font-medium text-foreground hover:text-accent transition-colors" data-testid="nav-categories">
              Categories
            </Link>
            <Link to="/community" className="text-sm font-medium text-foreground hover:text-accent transition-colors" data-testid="nav-community">
              Community
            </Link>
            <Link to="/about" className="text-sm font-medium text-foreground hover:text-accent transition-colors" data-testid="nav-about">
              About
            </Link>
          </nav>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="hidden lg:flex items-center flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                type="search"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-full"
                data-testid="search-input"
              />
            </div>
          </form>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Moon size={20} className="text-primary" />
              ) : (
                <Sun size={20} className="text-accent" />
              )}
            </Button>

            {isAuthenticated ? (
              <>
                {/* Wishlist */}
                <Link to="/wishlist" className="relative" data-testid="wishlist-button">
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <Heart size={20} />
                    {wishlistCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {wishlistCount}
                      </span>
                    )}
                  </Button>
                </Link>

                {/* Cart */}
                <Link to="/cart" className="relative" data-testid="cart-button">
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <ShoppingCart size={20} />
                    {cartCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-accent text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">
                        {cartCount}
                      </span>
                    )}
                  </Button>
                </Link>

                {/* Admin Dashboard Link */}
                {isAdmin && (
                  <Link to="/admin" data-testid="admin-dashboard-link">
                    <Button variant="ghost" size="icon" className="rounded-full">
                      <Shield size={20} className="text-accent" />
                    </Button>
                  </Link>
                )}

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="rounded-full h-8 w-8" />
                      ) : (
                        <User size={20} />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {user?.name && (
                      <>
                        <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" data-testid="user-dashboard-link" className="cursor-pointer flex items-center">
                        <LayoutDashboard size={16} className="mr-2" />
                        Dashboard &amp; Orders
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" data-testid="user-settings-link" className="cursor-pointer flex items-center">
                        <Settings size={16} className="mr-2" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer flex items-center">
                          <Shield size={16} className="mr-2" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="cursor-pointer flex items-center text-red-500 focus:text-red-500">
                      <LogOut size={16} className="mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="login-button">
                    Login
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="sm" className="btn-primary" data-testid="signup-button" style={{ background: 'var(--color-accent)', color: 'var(--color-text-on-accent)' }}>
                    Sign Up
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-4">
              <Link to="/products" className="text-sm font-medium text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Products
              </Link>
              <Link to="/categories" className="text-sm font-medium text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Categories
              </Link>
              <Link to="/community" className="text-sm font-medium text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                Community
              </Link>
              <Link to="/about" className="text-sm font-medium text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                About
              </Link>
              {isAuthenticated && (
                <>
                  <Link to="/wishlist" className="text-sm font-medium text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Wishlist ({wishlistCount})
                  </Link>
                  <Link to="/dashboard" className="text-sm font-medium text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Dashboard &amp; Orders
                  </Link>
                  <Link to="/settings" className="text-sm font-medium text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    Settings
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="text-sm font-medium text-accent hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                    className="text-sm font-medium text-left text-red-500 hover:text-red-600 transition-colors"
                    data-testid="mobile-logout-button"
                  >
                    Logout
                  </button>
                </>
              )}
              <form onSubmit={handleSearch} className="lg:hidden pt-2">
                <Input
                  type="search"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="rounded-full"
                />
              </form>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;